// 261010 P8b-2 — popover chuông: CTA điều hướng / không-AI; SOURCE_COVERAGE_GAP INFO không vào badge.
import React, { useMemo, useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import type { WorkflowStatus } from '../../types/filmWorkflow';
import { stepNameOf, truncateText } from './workflowBoardUtils';
import { visibleBellOf, type BellNav } from './workflowBellPolicy';
import { loadDismissedJobs } from './dismissedJobsStore';

interface Props {
  status: WorkflowStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dismissedJobIds?: string[];
  onAction: (nav: BellNav) => void;
  releaseError?: string | null;
}

const WorkflowBell: React.FC<Props> = ({ status, open, onOpenChange, dismissedJobIds, onAction, releaseError }) => {
  const [showSwallowed, setShowSwallowed] = useState(false);
  const dismissed = dismissedJobIds ?? loadDismissedJobs();
  const firstEventId = useMemo(() => {
    const evs = status?.events ?? [];
    const sorted = [...evs].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
    return sorted[0]?.eventId ?? null;
  }, [status]);
  const bell = visibleBellOf(status?.alerts ?? [], {
    firstEventId, dismissedJobIds: dismissed, jobsCount: status?.jobs.length ?? 0,
    sourceCoverage: status?.plan?.sourceCoverage,
  });
  const badge = bell.badgeText === '0' ? 0 : bell.badge;
  return (
    <Popover
      open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setShowSwallowed(false); }}
      trigger="click" placement="bottomRight"
      title={bell.empty ? 'Không có cảnh báo' : `${bell.total} cảnh báo (không chặn việc)`}
      content={(
        <div className="wf-bell-pop">
          {bell.empty && <span className="muted">Chưa có gì cần xem.</span>}
          {releaseError ? <div className="wf-alert ERROR" role="alert">{releaseError}</div> : null}
          {bell.items.map((it, i) => {
            if (it.kind === 'jobs') {
              return <div key={`jobs-${i}`} className="wf-alert INFO"><b>Đang chạy ở nền:</b> {it.jobsCount} việc. Trang tự cập nhật khi xong.</div>;
            }
            const a = it.alert!;
            return (
              <div key={i} className={`wf-alert ${a.severity}`}>
                <div className="wf-bell-msg" title={a.message}><b>{stepNameOf(a.stepKey)}</b> · {truncateText(a.message, 400)}</div>
                {a.code === 'SOURCE_COVERAGE_GAP' && showSwallowed && it.swallowed && it.swallowed.length > 0 ? (
                  <ul className="wf-bell-sw">
                    {it.swallowed.map((s, k) => <li key={k} title={s.text}>dòng {s.line}: {s.text}</li>)}
                  </ul>
                ) : null}
                {it.coverageNote && showSwallowed ? <p className="faint" style={{ fontSize: 12, margin: '4px 0 0' }}>{it.coverageNote}</p> : null}
                {it.buttons.length > 0 ? (
                  <div className="wf-bell-acts">
                    {it.buttons.map((b) => (
                      <button
                        key={b.key} type="button" className={`wf-btn${b.kind === 'ghost' ? ' ghost' : ''}`}
                        aria-label={b.label}
                        onClick={() => {
                          if (b.action.type === 'TOGGLE_SWALLOWED') { setShowSwallowed((x) => !x); return; }
                          onAction(b.action);
                        }}
                      >{b.action.type === 'TOGGLE_SWALLOWED' && showSwallowed ? 'Ẩn dòng bị bỏ' : b.label}</button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
          {bell.hiddenCount > 0 ? <div className="wf-bell-more">và {bell.hiddenCount} cảnh báo nữa</div> : null}
        </div>
      )}
    >
      <span className="wf-ib-w">
        <Tooltip title="Cảnh báo">
          <button type="button" className="wf-btn wf-ib wf-bell" aria-label={`Chuông cảnh báo, ${badge} mục cần xem`}>
            <BellOutlined />{badge > 0 && <span className="n">{bell.badgeText}</span>}
          </button>
        </Tooltip>
      </span>
    </Popover>
  );
};

export default WorkflowBell;
